import React, { useState, useEffect } from 'react';
import { Calendar, Plus, Edit2, Trash2, ExternalLink, Check, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    data: '',
    local: '',
    url: '',
    descricao: '',
    is_active: true
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/events/`);
      setEvents(response.data);
    } catch (error) {
      toast.error('Erro ao carregar eventos');
    } finally {
      setLoading(false);
    }
  };

  const seedEvents = async () => {
    try {
      await axios.post(`${API_URL}/api/events/seed`);
      toast.success('Eventos padrão criados');
      fetchEvents();
    } catch (error) {
      toast.error('Erro ao criar eventos padrão');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEvent) {
        await axios.put(`${API_URL}/api/events/${editingEvent.id}`, formData);
        toast.success('Evento atualizado');
      } else {
        await axios.post(`${API_URL}/api/events/`, formData);
        toast.success('Evento criado');
      }
      resetForm();
      fetchEvents();
    } catch (error) {
      toast.error('Erro ao salvar evento');
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      nome: event.nome,
      data: event.data,
      local: event.local,
      url: event.url || '',
      descricao: event.descricao || '',
      is_active: event.is_active
    });
    setShowForm(true);
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Tem certeza que deseja excluir este evento?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/events/${eventId}`);
      toast.success('Evento excluído');
      fetchEvents();
    } catch (error) {
      toast.error('Erro ao excluir evento');
    }
  };

  const toggleActive = async (event) => {
    try {
      await axios.put(`${API_URL}/api/events/${event.id}`, {
        is_active: !event.is_active
      });
      toast.success(event.is_active ? 'Evento desativado' : 'Evento ativado');
      fetchEvents();
    } catch (error) {
      toast.error('Erro ao alterar status');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingEvent(null);
    setFormData({
      nome: '',
      data: '',
      local: '',
      url: '',
      descricao: '',
      is_active: true
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Eventos BIM</h2>
          <p className="text-gray-400">Gerencie os eventos exibidos na homepage</p>
        </div>
        <div className="flex gap-2">
          {events.length === 0 && (
            <Button
              onClick={seedEvents}
              variant="outline"
              className="border-yellow-600 text-yellow-500 hover:bg-yellow-500/10"
            >
              Criar Eventos Padrão
            </Button>
          )}
          <Button
            onClick={() => setShowForm(true)}
            className="bg-red-600 hover:bg-red-700"
          >
            <Plus size={18} className="mr-2" />
            Novo Evento
          </Button>
        </div>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-white mb-4">
            {editingEvent ? 'Editar Evento' : 'Novo Evento'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Nome do Evento *</label>
                <Input
                  value={formData.nome}
                  onChange={(e) => setFormData({...formData, nome: e.target.value})}
                  placeholder="Ex: BIM World Paris"
                  required
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Data *</label>
                <Input
                  value={formData.data}
                  onChange={(e) => setFormData({...formData, data: e.target.value})}
                  placeholder="Ex: Abril 2026"
                  required
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Local *</label>
                <Input
                  value={formData.local}
                  onChange={(e) => setFormData({...formData, local: e.target.value})}
                  placeholder="Ex: São Paulo, Brasil"
                  required
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">URL do Site</label>
                <Input
                  value={formData.url}
                  onChange={(e) => setFormData({...formData, url: e.target.value})}
                  placeholder="https://..."
                  type="url"
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Descrição</label>
              <Input
                value={formData.descricao}
                onChange={(e) => setFormData({...formData, descricao: e.target.value})}
                placeholder="Breve descrição do evento"
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                className="rounded border-zinc-700"
              />
              <label htmlFor="is_active" className="text-sm text-gray-400">
                Evento ativo (visível na homepage)
              </label>
            </div>
            <div className="flex gap-2 pt-4">
              <Button type="submit" className="bg-red-600 hover:bg-red-700">
                {editingEvent ? 'Salvar Alterações' : 'Criar Evento'}
              </Button>
              <Button type="button" variant="outline" onClick={resetForm}>
                Cancelar
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Events List */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-zinc-800">
            <tr>
              <th className="text-left text-sm font-medium text-gray-400 px-4 py-3">Evento</th>
              <th className="text-left text-sm font-medium text-gray-400 px-4 py-3">Data</th>
              <th className="text-left text-sm font-medium text-gray-400 px-4 py-3">Local</th>
              <th className="text-left text-sm font-medium text-gray-400 px-4 py-3">Status</th>
              <th className="text-right text-sm font-medium text-gray-400 px-4 py-3">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {events.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                  Nenhum evento cadastrado
                </td>
              </tr>
            ) : (
              events.map((event) => (
                <tr key={event.id} className="hover:bg-zinc-800/50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Calendar size={16} className="text-purple-500" />
                      <span className="text-white font-medium">{event.nome}</span>
                      {event.url && (
                        <a
                          href={event.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-500 hover:text-purple-500"
                        >
                          <ExternalLink size={14} />
                        </a>
                      )}
                    </div>
                    {event.descricao && (
                      <p className="text-xs text-gray-500 mt-1">{event.descricao}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-300">{event.data}</td>
                  <td className="px-4 py-3 text-gray-300">{event.local}</td>
                  <td className="px-4 py-3">
                    <Badge
                      className={event.is_active 
                        ? "bg-green-600/20 text-green-400 border-green-500/30" 
                        : "bg-gray-600/20 text-gray-400 border-gray-500/30"
                      }
                    >
                      {event.is_active ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => toggleActive(event)}
                        className={event.is_active ? "text-yellow-500 hover:text-yellow-400" : "text-green-500 hover:text-green-400"}
                        title={event.is_active ? 'Desativar' : 'Ativar'}
                      >
                        {event.is_active ? <X size={16} /> : <Check size={16} />}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEdit(event)}
                        className="text-blue-500 hover:text-blue-400"
                      >
                        <Edit2 size={16} />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(event.id)}
                        className="text-red-500 hover:text-red-400"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminEvents;
